/**
 * useOfflineBook - React hook for DRM-aware offline book management
 *
 * Provides easy access to offline book functionality:
 * - Check if book is available offline
 * - Download selected chapters for offline reading (DRM-aware)
 * - Get offline book content
 * - Delete offline book
 * - Track download progress
 * - Check DRM quota and chapter availability
 */

import { useState, useEffect, useCallback } from 'react';
import { downloadManager } from '@/services/offline/DownloadManager';
import { offlineContentService } from '@/services/offline/OfflineContentService';
import { drmQuotaService } from '@/services/offline/DrmQuotaService';
import type {
  OfflineBook,
  DownloadProgress,
  DownloadResult,
  DownloadedChapterInfo,
  ChapterSelectionInfo,
} from '@/types/offline';
import type { BookMetadataDto } from '@/types/book';

export interface UseOfflineBookOptions {
  bookId: string;
  userId: string;
  bookMetadata?: BookMetadataDto | null; // Optional - hook will wait for metadata
  autoCheckAvailability?: boolean; // Default: true
}

export interface UseOfflineBookReturn {
  // State
  isAvailableOffline: boolean; // Has at least one chapter downloaded
  isDownloading: boolean;
  downloadProgress: DownloadProgress | null;
  offlineBook: OfflineBook | null;
  error: string | null;

  // DRM-aware state
  downloadedChapters: DownloadedChapterInfo[];
  downloadedChapterCount: number;
  availableChapterCount: number; // Based on DRM quota
  isDrmEnabled: boolean;
  quotaExceeded: boolean;

  // Actions
  downloadSelectedChapters: (chapterIndices: number[]) => Promise<DownloadResult>;
  downloadAllChapters: () => Promise<DownloadResult>; // For non-DRM books
  cancelDownload: () => void;
  deleteOffline: () => Promise<void>;
  refreshAvailability: () => Promise<void>;

  // DRM-aware actions
  getChapterSelectionInfo: () => Promise<ChapterSelectionInfo[]>;
  isChapterDownloaded: (chapterIndex: number) => Promise<boolean>;
}

export function useOfflineBook(options: UseOfflineBookOptions): UseOfflineBookReturn {
  const { bookId, userId, bookMetadata, autoCheckAvailability = true } = options;

  const [isAvailableOffline, setIsAvailableOffline] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);
  const [offlineBook, setOfflineBook] = useState<OfflineBook | null>(null);
  const [error, setError] = useState<string | null>(null);

  // DRM-aware state
  const [downloadedChapters, setDownloadedChapters] = useState<DownloadedChapterInfo[]>([]);
  const [downloadedChapterCount, setDownloadedChapterCount] = useState(0);
  const [availableChapterCount, setAvailableChapterCount] = useState(0);
  const [isDrmEnabled] = useState(bookMetadata?.isDrmEnabled ?? false);
  const [quotaExceeded, setQuotaExceeded] = useState(false);

  /**
   * Check if book is available offline (DRM-aware)
   */
  const checkAvailability = useCallback(async () => {
    try {
      // Check if book has any downloaded chapters
      const hasChapters = await offlineContentService.hasAnyDownloadedChapters(bookId);
      setIsAvailableOffline(hasChapters);

      if (hasChapters) {
        const book = await offlineContentService.getOfflineBook(bookId);
        setOfflineBook(book || null);
      } else {
        setOfflineBook(null);
      }

      // Get downloaded chapter info
      const chapters = await offlineContentService.getDownloadedChapters(bookId);
      setDownloadedChapters(chapters);
      setDownloadedChapterCount(chapters.length);

      // Check DRM quota
      if (isDrmEnabled) {
        try {
          const quotaInfo = await drmQuotaService.getQuota(userId, bookId);
          setAvailableChapterCount(quotaInfo.remainingQuota);
          setQuotaExceeded(quotaInfo.remainingQuota === 0);
        } catch (err) {
          console.error('Failed to check DRM quota:', err);
          setQuotaExceeded(true);
          setAvailableChapterCount(0);
        }
      } else {
        // No DRM - unlimited
        setAvailableChapterCount(999999);
        setQuotaExceeded(false);
      }

      // Check if currently downloading
      const currentlyDownloading = downloadManager.isDownloading(bookId);
      setIsDownloading(currentlyDownloading);

      if (currentlyDownloading) {
        const progress = await downloadManager.getProgress(bookId);
        setDownloadProgress(progress || null);
      }
    } catch (err) {
      console.error('Failed to check offline availability:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  }, [bookId, userId, isDrmEnabled]);

  /**
   * Download selected chapters for offline reading (DRM-aware)
   */
  const downloadSelectedChapters = useCallback(async (chapterIndices: number[]): Promise<DownloadResult> => {
    try {
      if (!bookMetadata) {
        throw new Error('Book metadata not loaded');
      }

      setError(null);
      setIsDownloading(true);

      const result = await downloadManager.downloadSelectedChapters(
        bookId,
        userId,
        chapterIndices,
        bookMetadata,
        (progress) => {
          setDownloadProgress(progress);
        }
      );

      if (result.success) {
        setIsAvailableOffline(true);
        await checkAvailability();
      } else {
        setError(result.error?.message || 'Download failed');
      }

      setIsDownloading(false);
      setDownloadProgress(null);

      return result;
    } catch (err) {
      console.error('Download failed:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setIsDownloading(false);
      setDownloadProgress(null);

      return {
        success: false,
        bookId,
        error: {
          code: 'UNKNOWN',
          message: err instanceof Error ? err.message : 'Unknown error',
          recoverable: false,
        },
      };
    }
  }, [bookId, userId, bookMetadata, checkAvailability]);

  /**
   * Download all chapters (for non-DRM books)
   */
  const downloadAllChapters = useCallback(async (): Promise<DownloadResult> => {
    if (!bookMetadata) {
      throw new Error('Book metadata not loaded');
    }
    const allChapterIndices = bookMetadata.chapters.map((_, index) => index);
    return downloadSelectedChapters(allChapterIndices);
  }, [bookMetadata, downloadSelectedChapters]);

  /**
   * Cancel active download
   */
  const cancelDownload = useCallback(() => {
    downloadManager.cancelDownload(bookId);
    setIsDownloading(false);
    setDownloadProgress(null);
    setError(null);
  }, [bookId]);

  /**
   * Delete offline book
   */
  const deleteOffline = useCallback(async () => {
    try {
      setError(null);
      await offlineContentService.deleteOfflineBook(bookId);
      setIsAvailableOffline(false);
      setOfflineBook(null);
    } catch (err) {
      console.error('Failed to delete offline book:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  }, [bookId]);

  /**
   * Refresh availability (manual trigger)
   */
  const refreshAvailability = useCallback(async () => {
    await checkAvailability();
  }, [checkAvailability]);

  /**
   * Get chapter selection info (for UI)
   */
  const getChapterSelectionInfo = useCallback(async (): Promise<ChapterSelectionInfo[]> => {
    if (!bookMetadata) {
      return [];
    }
    return offlineContentService.getDownloadableChaptersInfo(bookId, userId, bookMetadata);
  }, [bookId, userId, bookMetadata]);

  /**
   * Check if specific chapter is downloaded
   */
  const isChapterDownloadedCallback = useCallback(async (chapterIndex: number): Promise<boolean> => {
    return offlineContentService.isChapterDownloaded(bookId, chapterIndex);
  }, [bookId]);

  /**
   * Auto-check availability on mount and when bookId changes
   */
  useEffect(() => {
    if (autoCheckAvailability) {
      checkAvailability();
    }
  }, [autoCheckAvailability, checkAvailability]);

  return {
    // State
    isAvailableOffline,
    isDownloading,
    downloadProgress,
    offlineBook,
    error,

    // DRM-aware state
    downloadedChapters,
    downloadedChapterCount,
    availableChapterCount,
    isDrmEnabled,
    quotaExceeded,

    // Actions
    downloadSelectedChapters,
    downloadAllChapters,
    cancelDownload,
    deleteOffline,
    refreshAvailability,

    // DRM-aware actions
    getChapterSelectionInfo,
    isChapterDownloaded: isChapterDownloadedCallback,
  };
}
